import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { topic: "", content: "" };

export const KnowledgeBaseManager = () => {
  const [entries, setEntries] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchEntries = async () => {
    try {
      const { data } = await apiClient.get("/admin/knowledge-base");
      setEntries(data);
    } catch {
      toast.error("Could not load knowledge base entries");
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const openAddForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (entry) => {
    setEditingId(entry.id);
    setForm({ topic: entry.topic, content: entry.content });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.topic.trim() || !form.content.trim()) {
      toast.error("Topic and content are both required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiClient.put(`/admin/knowledge-base/${editingId}`, form);
        toast.success("Entry updated");
      } else {
        await apiClient.post("/admin/knowledge-base", form);
        toast.success("Entry added — the assistant will use it on its next reply");
      }
      closeForm();
      fetchEntries();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this entry from the assistant's knowledge base?")) return;
    try {
      await apiClient.delete(`/admin/knowledge-base/${id}`);
      toast.success("Entry removed");
      fetchEntries();
    } catch {
      toast.error("Could not remove entry");
    }
  };

  return (
    <div data-testid="admin-kb-manager">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#A1B0A6] max-w-md">
          Entries here are fed directly to the Compliance Assistant as authoritative, current
          knowledge — use this to keep the chatbot accurate as regulations or services change.
        </p>
        <button
          onClick={openAddForm}
          data-testid="admin-kb-add-btn"
          className="flex items-center gap-1.5 text-sm font-medium bg-gold hover:bg-gold-hover text-[#0A140F] rounded-full px-4 py-2 transition-colors shrink-0"
        >
          <Plus size={14} /> Add Entry
        </button>
      </div>

      {formOpen && (
        <div
          data-testid="admin-kb-form"
          className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 mb-6 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="font-display font-semibold text-[#F4F7F5]">
              {editingId ? "Edit Entry" : "New Entry"}
            </span>
            <button onClick={closeForm} data-testid="admin-kb-cancel-btn" className="text-[#A1B0A6] hover:text-gold">
              <X size={16} />
            </button>
          </div>
          <input
            value={form.topic}
            onChange={(e) => setField("topic", e.target.value)}
            placeholder="Topic (e.g. 'New GCT rate for electronics')"
            data-testid="admin-kb-input-topic"
            className="w-full bg-deep border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
          />
          <Textarea
            value={form.content}
            onChange={(e) => setField("content", e.target.value)}
            placeholder="Full detail the assistant should know about this topic"
            rows={4}
            data-testid="admin-kb-textarea-content"
            className="bg-deep border-gold/15 text-[#F4F7F5] placeholder:text-[#6E7E74]"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-kb-save-btn"
            className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] text-sm font-display font-semibold px-5 py-2 rounded-md transition-colors"
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add Entry"}
          </button>
        </div>
      )}

      {!entries ? (
        <p className="text-[#A1B0A6] text-sm">Loading entries...</p>
      ) : entries.length === 0 ? (
        <p className="text-[#A1B0A6] text-sm" data-testid="admin-kb-empty">
          No custom entries yet. The assistant is using its built-in baseline knowledge only.
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div
              key={e.id}
              data-testid={`admin-kb-card-${e.id}`}
              className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 flex items-start justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-semibold text-[#F4F7F5]">{e.topic}</span>
                  <span
                    data-testid={`admin-kb-usage-badge-${e.id}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      e.usage_count > 0 ? "bg-gold/20 text-gold" : "bg-[#6E7E74]/20 text-[#A1B0A6]"
                    }`}
                  >
                    {e.usage_count > 0 ? `Used ${e.usage_count}x` : "Not yet referenced"}
                  </span>
                </div>
                <p className="text-sm text-[#A1B0A6] mt-1 whitespace-pre-wrap">{e.content}</p>
                {e.last_used_at && (
                  <p className="text-xs text-[#6E7E74] mt-1">
                    Last used {new Date(e.last_used_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => openEditForm(e)}
                  data-testid={`admin-kb-edit-btn-${e.id}`}
                  className="text-[#A1B0A6] hover:text-gold p-1.5"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => handleDelete(e.id)}
                  data-testid={`admin-kb-delete-btn-${e.id}`}
                  className="text-[#A1B0A6] hover:text-red-400 p-1.5"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
